import { api } from '@/lib/api';

export interface HealthPrediction {
  id: string;
  client_external_id: string;
  client_source: string;
  client_name: string | null;
  predicted_at: string;
  churn_probability: number;
  health_score: number;
  /** The churn model states none; only the older rule scorer's rows carry one. */
  confidence: number | null;
  /** The churn model writes { label, weight }; the older rule scorer wrote the rest. */
  top_drivers: Array<{
    label: string;
    weight?: number;
    feature?: string;
    contribution?: number;
    rawValue?: number | null;
    reason?: string;
  }> | null;
}

/** Where a client model (churn, expansion) stands in the model registry, for this workspace. */
export interface ClientModelStanding {
  status: 'live' | 'champion' | 'candidate' | 'retired' | 'untrained';
  version: number | null;
  /** 'account' = this workspace's own model; 'global' = the shared one; null when untrained. */
  scope: 'global' | 'account' | null;
  /** Why it is not live, in the registry's words; for an untrained model, why the trainer declined to fit. */
  reason: string | null;
}

export type ClientModelStandings = Record<'churn_risk' | 'expansion_probability', ClientModelStanding>;

/** What a panel with no prediction can truthfully say: where the model stands, not "still training". */
export function whyNoPredictions(what: 'churn' | 'expansion', model?: ClientModelStanding | null): string {
  if (!model) return `No ${what} predictions to show.`;
  if (model.status === 'live') return `The ${what} model is live but has not scored your clients yet. It scores repeat clients with dated invoices or won deals.`;
  if (model.status === 'untrained') return model.reason ? `Not enough purchase history yet for a ${what} model: ${model.reason}.` : `No ${what} model has been trained yet.`;
  return `The ${what} model is not live${model.reason ? ` (${model.reason})` : ''}.`;
}

/** Whose model scored, for a page that names it: this workspace's own or the shared one. Null while none is live. */
export function whoseModel(model?: ClientModelStanding | null): string | null {
  if (model?.status !== 'live') return null;
  return model.scope === 'account' ? 'your model' : 'the shared model';
}

export interface HealthPredictionsResult {
  items: HealthPrediction[];
  summary: {
    total: number;
    highRisk: number;
    mediumRisk: number;
    healthy: number;
    avgConfidence: number | null;
  };
  /** Where the churn model stands for this workspace: whose it is, or why it is not live. */
  model?: ClientModelStanding | null;
}


export async function getHealthPredictions(): Promise<HealthPredictionsResult> {
  return api('/v1/client-health-predictor/predictions');
}
